/*
Main entrypoint for the widget script, that handles:
- loading the script (sync + async loading allowed)
- finding the root element relative to which the widget is deployed
- setting up the API handler
  (which is able to handle any additional actions called by the host window)
 */

import React from "react";
import Widget from "./components/Widget/Widget";
import Config, { DEFAULT_CONFIG } from "./config";
import { createRoot } from "react-dom/client";
import { checkUserUsage } from "./api/checkUsage";

const currentScript = document.currentScript;
const widgetName =
  currentScript.getAttribute("data-widget-name") || Config.name;
const widgetConfigName = widgetName + "Config";
let widgetComponent = null;

async function app(window) {
  // If we don't already have a name for widget's global object
  // assigned by the host, then they must be using the simple <script> tag method
  // so we need to get our data out of that tag
  if (!window[widgetName]) {
    // this is the synchronous path
    // need to store values from script in a placeholder object
    let tag = document.getElementById(widgetName + "-Script");

    if (!tag) {
      throw Error(`Cannot find script tag with id ${widgetName}-Script`);
    }

    const rawData = tag.getAttribute("data-config");
    const data = JSON.parse(rawData);

    window[widgetName] = data.name;

    let placeholder = {};
    (placeholder.q = []).push(["init", data.config]);

    window[window[widgetName]] = placeholder;
  }

  let placeholder = window[window[widgetName]];

  // override temporary (until the app loaded) handler
  // for widget's API calls
  window[window[widgetName]] = apiHandler;
  window[widgetConfigName] = DEFAULT_CONFIG;

  if (placeholder) {
    let queue = placeholder.q;
    if (queue) {
      for (var i = 0; i < queue.length; i++) {
        await apiHandler(queue[i][0], queue[i][1]);
      }
    }
  }

  window.addEventListener(
    "message",
    /**
     * @param {MessageEvent<{source: string, params: string[]}>} message
     */
    async (message) => {
      // only handle messages from our method
      if (message.data.source !== "chat-widget-iframe-helper") {
        return;
      }

      const params = message.data.params;

      if (params.length < 1) {
        return;
      }

      if (params[0] === "init") {
        return;
      }

      try {
        switch (params[0].toLowerCase()) {
          case "togglechatopen":
          case "setchatopenstate":
            throw new Error(
              "Modifying open state is not supported for iframe widgets",
            );
          case "patchconfigoverride":
            if (params[1] === "widgetType") {
              throw new Error(
                "Modifying widget type is not supported for iframe widgets",
              );
            }
            break;
        }

        await apiHandler(...params);
      } catch (err) {
        console.error("Error in iframe message handler:", err.message);
      }
    },
    false,
  );
}

/**
 Method that handles all API calls
 */
async function apiHandler(api, params, params2 = undefined) {
  try {
    if (!api) throw Error("API method required");
    api = api.toLowerCase();
    let config = window[widgetConfigName]; // starts as DEFAULT_CONFIG

    switch (api) {
      case "init":
        /*
        This is where we create the Widget component and render it where the
        target div has been Params here are the values in the config object
        passed into the script tag
        */

        const {
          render_widget,
          entitlements,
          embed_config: serverConfig,
        } = await checkUserUsage(params.userId, params.stackId);

        if (!render_widget) {
          return;
        }

        // merge default config and params passed to script
        // Precedence:
        // values from script tag > config retrieved from server > default values
        config = {
          // currently config loads the default config from config.js
          ...config, //equal to DEFAULT_CONFIG before merge
          ...serverConfig,
          ...params,
          // allow for merging of nested attributes
          colors: {
            // merge colors so that at the least nothing is missing
            ...config.colors,
            ...serverConfig.colors,
            ...params.colors,
          },
        };
        console.log("loading: ", widgetName, config);

        config["entitlements"] = entitlements;
        // config.websockets = embed_config.websockets || config.websockets;
        config.websockets = false;

        window[widgetConfigName] = config;

        // get a reference to the created widget component,
        // so we can call methods as needed
        widgetComponent = React.createRef();
        const magicFormRootElement = document.getElementById(
          config.targetElementId || "magicform-root",
        );
        if (magicFormRootElement) {
          // render widget in element passed in config, defaults to root
          createRoot(magicFormRootElement).render(
            <Widget
              deepRef={widgetComponent}
              widgetConfigName={widgetConfigName}
            />,
          );
        }
        break;
      case "message":
        // Send the message to the current widget instance
        widgetComponent.current.setMessage(params);
        break;
      case "resetwidgetcontext":
        widgetComponent.current.resetWidgetContext();
        break;
      case "setchatcontext":
        widgetComponent.current.setChatContextToStorage(params);
        break;
      case "setlanguageoverride":
        widgetComponent.current.setLanguageOverride(params);
        break;
      case "resetlanguageoverride":
        widgetComponent.current.resetLanguageOverride();
        break;
      case "togglechatopen":
        widgetComponent.current.toggleChatOpen();
        break;
      case "setchatopenstate":
        widgetComponent.current.setChatOpenState(params);
        break;
      case "patchconfigoverride":
        widgetComponent.current.patchConfigOverride(params, params2);
        break;
      case "patchcoloroverride":
        widgetComponent.current.patchColorOverride(params, params2);
        break;
      case "resetalloverrides":
        widgetComponent.current.resetAllOverrides();
        break;
      default:
        throw new Error(`Method ${api} is not supported`);
    }
  } catch (err) {
    console.error("Error in apiHandler:", err.message);
  }
}

app(window);

export default app;
